/**
 * Encapsulates the save logic for the Schedule Interview wizard.
 * Handles phone-edit side effects, multipart vs JSON payload routing,
 * mailbox pre-flight, and error mapping for all four mutation paths
 * (create/edit × manual/self-schedule).
 */
import { useCallback } from 'react';
import { useCreateInterview, useUpdateInterview } from '@/api/useInterviews';
import {
  useCreateBulkSelfSchedule,
  useCreateSelfSchedule,
  useUpdateSelfSchedule,
} from '@/api/useSelfSchedule';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue/useBulkOperation';
import { buildPayload, buildPayloadFormData } from '../payload';
import type { BulkSelfScheduleContextIds } from '../payload/types';
import { api } from '@/api/request';
import { SCHEDULE_INTERVIEW_COPY } from '../config/copy';
import { resolveScheduleErrorMessage } from '../config/errorCopy';
import { isBulkSelfScheduleMode } from '../config/modeClassifiers';
import type {
  ScheduleMode,
  ScheduleInterviewPayload,
  InterviewUpdateFlags,
  PhoneValue,
} from '../config/types';

export type { PhoneValue };

function phoneValuesEqual(
  a: PhoneValue | null | undefined,
  b: PhoneValue | null | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.dialCode ?? '').trim() === (b.dialCode ?? '').trim() &&
    (a.number ?? '').trim() === (b.number ?? '').trim()
  );
}

interface UseScheduleSaveOptions {
  mode: ScheduleMode;
  isEdit: boolean;
  isSelfSchedule: boolean;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  initialCandidatePhone?: PhoneValue;
  /**
   * Multi-candidate target list + source job id. Present only when
   * `isBulkSelfScheduleMode(mode)` and the prerequisite overlay has
   * resolved. Drives the `POST /ats_schedules/bulk` branch.
   */
  bulkSelfScheduleContext?: BulkSelfScheduleContextIds;
  onClose?: () => void;
  onSuccess?: () => void;
}

interface UseScheduleSaveResult {
  handleComplete: (
    _payload: ScheduleInterviewPayload,
    _flags?: InterviewUpdateFlags
  ) => Promise<void>;
  isSaving: boolean;
}

export function useScheduleSave({
  mode,
  isEdit,
  isSelfSchedule,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  initialCandidatePhone,
  bulkSelfScheduleContext,
  onClose,
  onSuccess,
}: UseScheduleSaveOptions): UseScheduleSaveResult {
  const toast = useShowToast();
  // Account-level FE flag controlling HTML-to-text post-processing of
  // calendar-invite descriptions. Read once per render so the
  // save-time mappers see the current value. See `mapCommunication.ts`
  // `stripHtmlWhenUnformatted` for the actual conversion.
  const { feHtmlToText } = useKFlags();

  const isBulk = isBulkSelfScheduleMode(mode);
  // Bulk-self-schedule queues a server-side `BulkActivityWorker`
  // (see `useBulkSelfSchedule` JSDoc); the queue panel polls the
  // bulk-activities endpoint to surface progress. Calling
  // `triggerBulkActivitiesFetch` from `completeSuccess` is what makes
  // the new entry show up immediately after the API ack.
  const bulkQueue = useBulkOperation({
    activityType: 'send_self_schedule',
    title: 'Sending self-schedule emails',
  });

  const createInterview = useCreateInterview();
  const updateInterview = useUpdateInterview();
  const createSelfSchedule = useCreateSelfSchedule();
  const updateSelfSchedule = useUpdateSelfSchedule();
  const createBulkSelfSchedule = useCreateBulkSelfSchedule();

  const isSaving =
    createInterview.isPending ||
    updateInterview.isPending ||
    createSelfSchedule.isPending ||
    updateSelfSchedule.isPending ||
    createBulkSelfSchedule.isPending;

  const handleComplete = useCallback(
    async (payload: ScheduleInterviewPayload, flags?: InterviewUpdateFlags) => {
      const contextIds = {
        ats_application_id: applicationId,
        ats_job_id: jobId,
        ats_job_stage_id: stageId,
        ats_job_stage_action_id: stageActivityId ?? null,
        candidate_id: candidateId,
      };

      // /ats_interviews wants multipart so attachments can carry File blobs.
      // /ats_schedules does its own convertToFormData inside useSelfSchedule.
      const featureFlags = { feHtmlToText: Boolean(feHtmlToText) };
      const jsonPayload = buildPayload(
        payload,
        contextIds,
        mode,
        flags,
        featureFlags,
        isBulk ? bulkSelfScheduleContext : undefined
      );
      const interviewFormData = buildPayloadFormData(
        payload,
        contextIds,
        mode,
        flags,
        featureFlags
      );
      const selfScheduleJson = jsonPayload;

      try {
        // Phone edit → contact update. Only fires when the user actually
        // opened the pencil AND the new value differs from initialCandidatePhone.
        const editedPhone = payload.details.candidate_phone as
          | PhoneValue
          | null
          | undefined;
        const phoneWasEdited =
          payload.details.isCandidatePhoneEditing === 'edit' &&
          !phoneValuesEqual(editedPhone, initialCandidatePhone);
        if (
          phoneWasEdited &&
          typeof candidateId === 'number' &&
          candidateId > 0 &&
          editedPhone?.number
        ) {
          await api.put(
            `/contacts/${candidateId}`,
            {
              phone_code: editedPhone.dialCode ?? '',
              phone_number: editedPhone.number ?? '',
            },
            { client: 'api' }
          );
        }

        if (isBulk) {
          // Bulk is always create — there's no edit path for a fanned-out
          // self-schedule. Local queue entry first so the modal appears
          // immediately, then the API call queues the worker; completeSuccess
          // triggers the bulk-activities fetch that hands progress to the
          // queue panel.
          const total = bulkSelfScheduleContext?.applicationIds.length ?? 0;
          bulkQueue.startTracking(total);
          await createBulkSelfSchedule.mutateAsync({ payload: jsonPayload });
          bulkQueue.completeSuccess(total);
        } else if (isSelfSchedule) {
          if (isEdit && scheduleId) {
            await updateSelfSchedule.mutateAsync({
              scheduleId,
              payload: selfScheduleJson,
            });
          } else {
            await createSelfSchedule.mutateAsync({ payload: selfScheduleJson });
          }
        } else {
          if (isEdit && interviewId) {
            await updateInterview.mutateAsync({
              interviewId,
              payload: interviewFormData,
            });
          } else {
            await createInterview.mutateAsync({ payload: interviewFormData });
          }
        }

        // Bulk: the BulkOperationQueue panel surfaces progress + completion
        // — a wizard toast on top would double-notify. Self-schedule edit:
        // the outer wrapper (SelfSchedule.tsx) fires its own toast in
        // onSuccess — skip ours to avoid showing two.
        const outerHandlesToast = isSelfSchedule && isEdit;
        if (!outerHandlesToast && !isBulk) {
          toast({
            title: isEdit
              ? SCHEDULE_INTERVIEW_COPY.toast.updateSuccess.title
              : SCHEDULE_INTERVIEW_COPY.toast.createSuccess.title,
            status: 'success',
          });
        }
        // Bulk skips onSuccess — same as legacy `KInterviewScheduler`
        // bulk path (`Scheduler.tsx` lines 712-714: "Don't call
        // onSuccess for bulk operations - no toast needed. The bulk
        // operation queue handles the feedback"). Calling it here
        // would trigger the parent `JobCandidates.schedulerOnSuccess`
        // which reads `apiReference` (undefined when called from the
        // new wizard) and toasts the wrong "updated" title.
        if (!isBulk) {
          onSuccess?.();
        }
        onClose?.();
      } catch (err) {
        if (isBulk) {
          // Mark the queue entry failed so the panel doesn't sit forever
          // on "in progress" after a 4xx/5xx.
          bulkQueue.completeFailed();
        }
        // Title-only toast: when we have a specific err_* code mapped in
        // SCHEDULE_ERROR_COPY (or the legacy `Modules/schedule.js`),
        // surface that message as the title. Fall back to the generic
        // copy for unknown failures.
        toast({
          title: resolveScheduleErrorMessage(
            err,
            SCHEDULE_INTERVIEW_COPY.toast.saveError.fallback
          ),
          status: 'error',
        });
      }
    },
    [
      mode,
      applicationId,
      candidateId,
      initialCandidatePhone,
      jobId,
      stageId,
      stageActivityId,
      createInterview,
      createSelfSchedule,
      createBulkSelfSchedule,
      interviewId,
      isEdit,
      isSelfSchedule,
      isBulk,
      bulkSelfScheduleContext,
      bulkQueue,
      onClose,
      onSuccess,
      scheduleId,
      toast,
      updateInterview,
      updateSelfSchedule,
      feHtmlToText,
    ]
  );

  return { handleComplete, isSaving };
}
